import React, { useMemo } from 'react';
import VmkQuotePricingPage from './VmkQuotePricingPage';
import { VmkQuoteTemplateFront, VmkQuoteTemplateBack, VmkAgreementPage } from './VmkQuoteTemplatePages';
import { packQuotesIntoPages } from '../lib/vmkQuotePagePack';
import { getDrawingPageNumber, getTailPageNumber } from '../lib/vmkQuoteDocumentLayout';
import { getTotalQuotesAreaSqm } from '../lib/vmkQuoteTable';
import { enhanceQuoteDrawingSvg } from '../lib/enhanceQuoteDrawingSvg';
import { normalizeBackendQuoteDrawings, normalizeQuoteSections } from '../lib/quoteSession';
import { getQuoteProjectFeesExVat, BTW_MULTIPLIER_INCL } from '../lib/pdfPricing';
import { applyExportPriceAdjustment, getQuoteExtraLineItemsExVat } from '../lib/pricingAdjustments';
import {
  buildExportSectionList,
  computeDrawingsStoredTotalExVat,
  countQuoteDrawings,
  isMultiSectionQuote,
} from '../lib/quoteSections';

const getQuoteLineTotalExVat = (quote) => {
  const qty = quote.config?.quantity || quote.quantity || 1;
  if (quote.pricing?.subtotal != null) return Number(quote.pricing.subtotal);
  return (quote.pricing?.totalPrice || 0) * qty;
};

const getQuoteLineTotalInclVat = (quote) => getQuoteLineTotalExVat(quote) * BTW_MULTIPLIER_INCL;

const formatPrice = (price) =>
  new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);

const getSvgWithoutBackground = (svgString) => {
  if (!svgString || typeof svgString !== 'string') return '';
  const cleaned = svgString.replace(/\bfill="#[fF]5[fF]5[fF]5"/g, 'fill="#FFF"');
  return enhanceQuoteDrawingSvg(cleaned);
};

/**
 * Off-screen VMK export (cover → drawings → investment → agreement) for view-quote PDF download.
 * @param {boolean} [includeAgreementPage=true] — set false for on-screen / pre-sign review so the
 *   empty signing page is only in the completed PDF / post-sign email.
 */
export const ViewQuotePdfDocument = ({
  quoteNumber,
  clientDetails = {},
  drawings = [],
  sections = [],
  exportOptions = {},
  subtotal,
  signedAgreement = null,
  includeAgreementPage = true,
  t,
}) => {
  const quoteSections = useMemo(
    () => normalizeQuoteSections({ sections }),
    [sections],
  );

  const savedQuotes = useMemo(
    () => normalizeBackendQuoteDrawings({ drawings }),
    [drawings],
  );

  const includePricingInExport = exportOptions.includePricingInExport !== false;
  const includeTemplateInExport = exportOptions.includeTemplateInExport !== false;
  const hasQuoteContent = countQuoteDrawings(quoteSections, savedQuotes) > 0;
  const multiSectionMode = isMultiSectionQuote(quoteSections, savedQuotes);
  const showVmkTemplatePages = includeTemplateInExport && hasQuoteContent;

  const exportSections = useMemo(
    () =>
      buildExportSectionList(quoteSections, savedQuotes, {
        currentSectionName: t('summary.unnamedSection'),
      }),
    [quoteSections, savedQuotes, t],
  );

  const sectionPagePlans = useMemo(
    () =>
      exportSections
        .filter((section) => (section.drawings?.length ?? 0) > 0)
        .map((section) => ({
          ...section,
          chunks: packQuotesIntoPages(section.drawings, {
            includePricing: includePricingInExport,
            reserveTailSpace: includePricingInExport,
            tailReserveHeight: showVmkTemplatePages ? 95 : undefined,
          }),
        })),
    [exportSections, includePricingInExport, showVmkTemplatePages],
  );

  const quoteChunks = useMemo(
    () => sectionPagePlans.flatMap((section) => section.chunks),
    [sectionPagePlans],
  );

  const subtotalExVatBeforeExportDeduction =
    savedQuotes.reduce((sum, quote) => sum + getQuoteLineTotalExVat(quote), 0) +
    getQuoteProjectFeesExVat(clientDetails) +
    getQuoteExtraLineItemsExVat(clientDetails);
  const totalPriceExVat =
    typeof subtotal === 'number' && subtotal > 0
      ? applyExportPriceAdjustment(subtotal, clientDetails)
      : applyExportPriceAdjustment(subtotalExVatBeforeExportDeduction, clientDetails);
  const totalPrice = totalPriceExVat;
  const allDrawingsForTemplate = useMemo(
    () => exportSections.flatMap((section) => section.drawings || []),
    [exportSections],
  );
  const totalArea = getTotalQuotesAreaSqm(
    allDrawingsForTemplate.length > 0 ? allDrawingsForTemplate : savedQuotes,
  );
  const advisorName = t('quote.template.defaultAdvisor');

  const sectionSummaries = useMemo(() => {
    if (!multiSectionMode) return null;
    const sectionsWithDrawings = exportSections.filter((section) => (section.drawings?.length ?? 0) > 0);
    const lastIndex = sectionsWithDrawings.length - 1;
    return sectionsWithDrawings.map((section, sectionIndex) => {
      let sectionTotalExVat = computeDrawingsStoredTotalExVat(section.drawings);
      const isLastSection = sectionIndex === lastIndex;
      if (isLastSection) {
        sectionTotalExVat += getQuoteProjectFeesExVat(clientDetails);
        sectionTotalExVat += getQuoteExtraLineItemsExVat(clientDetails);
        sectionTotalExVat = applyExportPriceAdjustment(sectionTotalExVat, clientDetails);
      }
      return {
        id: section.id,
        name: section.name || t('summary.unnamedSection'),
        totalInclVat: sectionTotalExVat * BTW_MULTIPLIER_INCL,
        areaSqm: getTotalQuotesAreaSqm(section.drawings),
      };
    });
  }, [multiSectionMode, exportSections, clientDetails, t]);

  const drawingPageCount = quoteChunks.length;
  const templatePageNumbers = useMemo(() => {
    if (!showVmkTemplatePages) return null;
    return {
      cover: 1,
      proposal: 2,
      specs: 3,
      investment: getTailPageNumber(drawingPageCount, 0),
      warranty: getTailPageNumber(drawingPageCount, 1),
      process: getTailPageNumber(drawingPageCount, 2),
      sustainability: getTailPageNumber(drawingPageCount, 3),
      agreement: getTailPageNumber(drawingPageCount, 4),
    };
  }, [showVmkTemplatePages, drawingPageCount]);

  if (!hasQuoteContent) {
    if (!includeAgreementPage) {
      return null;
    }
    return (
      <VmkAgreementPage
        pageNumber={1}
        clientDetails={clientDetails}
        signedAgreement={signedAgreement}
        t={t}
      />
    );
  }

  const vmkPricingSharedProps = {
    quoteNumber,
    clientName: clientDetails.name,
    tableSvgData: null,
    getSvgWithoutBackground,
    getQuoteLineTotal: getQuoteLineTotalInclVat,
    formatPrice,
    t,
    isEditing: false,
    isExportingPDF: true,
    removeQuote: () => {},
    setTableSvgData: () => {},
    savedQuotes,
    totalPrice,
    subtotalExVatBeforeExportDeduction,
    exportDeductionExVat: 0,
    exportDeductionInclVat: 0,
    deductionLabel: t('quote.exportDeductionLine'),
    deductionLabelExVat: t('quote.exportDeductionLineExVat'),
    showPricing: includePricingInExport,
  };

  const vmkPricingPages = (() => {
    let globalPageIndex = 0;
    let globalDrawingIndex = 0;
    let showIntroOnce = true;
    const lastSectionIndex = sectionPagePlans.length - 1;

    return sectionPagePlans.flatMap((section, sectionIndex) => {
      const isLastSection = sectionIndex === lastSectionIndex;
      let sectionTotalExVat = computeDrawingsStoredTotalExVat(section.drawings);
      if (multiSectionMode) {
        if (isLastSection) {
          sectionTotalExVat += getQuoteProjectFeesExVat(clientDetails);
          sectionTotalExVat += getQuoteExtraLineItemsExVat(clientDetails);
          sectionTotalExVat = applyExportPriceAdjustment(sectionTotalExVat, clientDetails);
        }
      } else {
        sectionTotalExVat = totalPriceExVat;
      }

      const sectionTotalsTitle = multiSectionMode && section.name
        ? t('quote.sectionTotalNamed', { name: section.name })
        : null;

      return section.chunks.map((chunk, chunkIndex) => {
        const isLastChunkOfSection = chunkIndex === section.chunks.length - 1;
        const runningGlobalFirstIndex = globalDrawingIndex;
        const page = (
          <VmkQuotePricingPage
            key={`${section.id}-${chunk.map((q) => q.id).join('-') || chunkIndex}`}
            {...vmkPricingSharedProps}
            chunk={chunk}
            globalFirstIndex={runningGlobalFirstIndex}
            compact={chunk.length > 1}
            showIntro={showIntroOnce}
            showSectionHeader={Boolean(section.name) && chunkIndex === 0}
            sectionName={section.name}
            sectionIndex={multiSectionMode ? sectionIndex : null}
            showTotals={isLastChunkOfSection && includePricingInExport}
            showLegal={false}
            sectionTotalsTitle={sectionTotalsTitle}
            totalPrice={sectionTotalExVat}
            subtotalExVatBeforeExportDeduction={sectionTotalExVat}
            exportDeductionExVat={0}
            exportDeductionInclVat={0}
            pageNumber={
              showVmkTemplatePages ? getDrawingPageNumber(globalPageIndex) : globalPageIndex + 1
            }
            useBrandedLayout={showVmkTemplatePages}
          />
        );
        globalPageIndex += 1;
        globalDrawingIndex += chunk.length;
        showIntroOnce = false;
        return page;
      });
    });
  })();

  return (
    <>
      {showVmkTemplatePages && templatePageNumbers && (
        <VmkQuoteTemplateFront
          pageNumbers={templatePageNumbers}
          quoteNumber={quoteNumber}
          clientDetails={clientDetails}
          advisorName={advisorName}
          savedQuotes={allDrawingsForTemplate}
          t={t}
        />
      )}
      {vmkPricingPages}
      {showVmkTemplatePages && templatePageNumbers ? (
        <VmkQuoteTemplateBack
          pageNumbers={templatePageNumbers}
          quoteNumber={quoteNumber}
          clientDetails={clientDetails}
          totalPrice={totalPrice}
          totalAreaSqm={totalArea}
          formatPrice={formatPrice}
          getQuoteLineTotal={getQuoteLineTotalInclVat}
          savedQuotes={allDrawingsForTemplate}
          sectionSummaries={sectionSummaries}
          multiSectionMode={multiSectionMode}
          signedAgreement={signedAgreement}
          includeAgreementPage={includeAgreementPage}
          t={t}
        />
      ) : includeAgreementPage ? (
        <VmkAgreementPage
          pageNumber={drawingPageCount + 1}
          clientDetails={clientDetails}
          signedAgreement={signedAgreement}
          t={t}
        />
      ) : null}
    </>
  );
};
